import {
  handleGetGlobalRealtimeSearchAction,
  handleGetGlobalSearchAction,
  handleGetGlobalSearchSuggestionsAction,
  handleGetGlobalTrendingSearchAction,
} from "@/actions/global-search.action";
import {
  GlobalRealtimeSearchRes,
  GlobalSearchRes,
  GlobalSearchSuggesstionRes,
  GlobalTrendingSearchRes,
} from "@/components/layout/header/global-search/global-search.type";
import { ApiResult } from "@/types/api";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useHotelQuery } from "./url-state/useHotelQuery";
import { useRentalQuery } from "./url-state/useRentalQuery";
import { useCallback } from "react";

const SEARCH_STALE_TIME = 1000 * 60 * 30;
const SEARCH_GC_TIME = 1000 * 60 * 60;

const searchQueryOptions = {
  staleTime: SEARCH_STALE_TIME,
  gcTime: SEARCH_GC_TIME,
  refetchOnWindowFocus: false,
};

/** ******************************
 * Global Search Suggestions
 ****************************** */
const useGlobalSearchSuggestions = (
  enabled: boolean
): UseQueryResult<GlobalSearchSuggesstionRes, Error> => {
  return useQuery({
    queryKey: ["global-search-suggestions"],
    queryFn: handleGetGlobalSearchSuggestionsAction,
    ...searchQueryOptions,
    enabled,
    select: (res: ApiResult<GlobalSearchSuggesstionRes>) => res.data,
  });
};

/** ******************************
 * Global Realtime Search
 ****************************** */
const useGlobalRealtimeSearch = (
  keyword: string,
  enabled = true
): UseQueryResult<GlobalRealtimeSearchRes[], Error> => {
  const trimmedKeyword = keyword.trim();

  return useQuery({
    queryKey: ["global-realtime-search", trimmedKeyword],
    queryFn: () => handleGetGlobalRealtimeSearchAction(trimmedKeyword),
    ...searchQueryOptions,
    enabled: enabled && !!trimmedKeyword,
    select: (res: ApiResult<GlobalRealtimeSearchRes[]>) => res.data,
  });
};

/** ******************************
 * Global Search
 ****************************** */
const useGlobalSearch = (
  keyword: string,
  locationId?: number | null,
  enabled = true
): UseQueryResult<GlobalSearchRes, Error> => {
  const trimmedKeyword = keyword.trim();

  return useQuery({
    queryKey: locationId
      ? ["global-search-destination", trimmedKeyword, locationId]
      : ["global-search-keyword", trimmedKeyword],
    queryFn: () => handleGetGlobalSearchAction(trimmedKeyword, locationId ?? undefined),
    ...searchQueryOptions,
    enabled: enabled && (!!trimmedKeyword || !!locationId),
    select: (res: ApiResult<GlobalSearchRes>) => res.data,
  });
};

/** ******************************
 * Trending Search
 ****************************** */
const useGlobalTrendingSearch = (
  locationId: number | null,
  enabled = true
): UseQueryResult<GlobalTrendingSearchRes, Error> => {
  return useQuery({
    queryKey: ["global-trending-search", locationId],
    queryFn: () => handleGetGlobalTrendingSearchAction(locationId),
    ...searchQueryOptions,
    enabled: enabled && !!locationId,
    select: (res: ApiResult<GlobalTrendingSearchRes>) => res.data,
  });
};


/** ******************************
 * Global Search Type Path
 ****************************** */
const TYPE_PATH: [string, string][] = [
  ["hotel", "/hotels"],
  ["rental", "/rentals"],
  ["tour_act", "/tour-act"],
  ["spa_msg", "/spa-msg"],
  ["location", "/global-search"],
];
const TYPE_PATH_MAP = new Map(TYPE_PATH);
type SearchItem = { id: string | number; type: string };

function useSearchItemHref() {
  const hotelQuery = useHotelQuery();
  const rentalQuery = useRentalQuery();

  return useCallback(
    (item: SearchItem): string | undefined => {
      const path = TYPE_PATH_MAP.get(item.type);
      if (!path) return undefined;

      if (item.type === "location") {
        const params = new URLSearchParams({ locationId: `${item.id}` });
        return `${path}?${params}`;
      }

      const params = new URLSearchParams();

      if (item.type === "hotel") {
        params.set("adults", `${hotelQuery.adults}`);
        params.set("children", `${hotelQuery.children}`);
        params.set("rooms", `${hotelQuery.rooms}`);
        params.set("type", "popular");
        if (hotelQuery.start) params.set("start", hotelQuery.start);
        if (hotelQuery.end) params.set("end", hotelQuery.end);
      } else {
        if (rentalQuery.start) params.set("start", rentalQuery.start);
        if (item.type === "rental") {
          if (rentalQuery.locationId) params.set("locationId", rentalQuery.locationId);
          if (rentalQuery.lat && rentalQuery.lng) {
            params.set("lat", rentalQuery.lat);
            params.set("lng", rentalQuery.lng);
          }
          if (rentalQuery.pickupType) params.set("pickupType", rentalQuery.pickupType);
        }
      }

      return params.size ? `${path}/${item.id}?${params}` : `${path}/${item.id}`;
    },
    [hotelQuery, rentalQuery],
  );
}


export {
  useGlobalSearchSuggestions,
  useGlobalRealtimeSearch,
  useGlobalSearch,
  useGlobalTrendingSearch,
  useSearchItemHref,
};